import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CancelInterviewRequest,
  CancelInterviewResponse,
  HostLinkResponse,
  IntegrationCredentialListResponse,
  MintMeetingRequest,
  MintMeetingResponse,
  OnlineInterviewListResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase F13 FE-1 — TanStack hooks for the online interview admin
 * surface (BE-1c) plus the integrations credential listing (BE-1b)
 * needed to populate the host-credential picker on the mint dialog.
 *
 * Cache topology
 * --------------
 * ``onlineInterviewKeys.all`` is the umbrella; bust it after any
 * mutation (mint / cancel) so a stage-completion change driven by a
 * downstream evaluator (interview_completed) re-fetches in concert
 * with the proctor / stage-status keys.
 *
 * Privacy notes
 * -------------
 * Mint returns the host ``start_url`` exactly once. We surface it
 * in the immediate-success toast (clipboard-copy CTA) and DO NOT
 * persist it in TanStack cache — the consumer should treat the
 * mutation result as ephemeral. A subsequent render of the
 * interview row will fetch it again via :func:`useHostLink`,
 * which the BE audit-logs.
 */
export const onlineInterviewKeys = {
  all: ["online-interview"] as const,
  forEnquiry: (enquiryId: string | null, stageKey?: string | null) =>
    [...onlineInterviewKeys.all, "for-enquiry", enquiryId ?? "_none", stageKey ?? "_any"] as const,
  credentials: (provider: string | null) =>
    [...onlineInterviewKeys.all, "credentials", provider ?? "_all"] as const,
};

/**
 * List integration credentials available to the calling tenant.
 * Used by the mint dialog to populate the host-credential picker
 * — only ACTIVE rows are usable; the dialog still shows
 * non-active ones with a disabled state so the IA understands
 * why a stale account isn't selectable.
 */
export function useIntegrationCredentials(provider: "zoom" | "google_meet" | null = "zoom") {
  return useQuery({
    queryKey: onlineInterviewKeys.credentials(provider),
    queryFn: () => {
      const qs = provider ? `?provider=${encodeURIComponent(provider)}` : "";
      return api.get<IntegrationCredentialListResponse>(`/admin/integrations/credentials${qs}`);
    },
    staleTime: 30_000,
  });
}

/**
 * List every online interview tied to one enquiry. Most-recently-
 * scheduled first. Cancelled / errored rows ARE included so the
 * IA can audit a failed mint or a rescheduled session.
 */
export function useEnquiryInterviews(enquiryId: string | null, stageKey: string | null = null) {
  return useQuery({
    queryKey: onlineInterviewKeys.forEnquiry(enquiryId, stageKey),
    queryFn: () => {
      const qs = stageKey ? `?stage_key=${encodeURIComponent(stageKey)}` : "";
      return api.get<OnlineInterviewListResponse>(
        `/admin/admissions/${encodeURIComponent(enquiryId ?? "")}/interviews${qs}`,
      );
    },
    enabled: Boolean(enquiryId),
    staleTime: 15_000,
  });
}

/**
 * Mint a Zoom meeting for an enquiry's interview stage.
 *
 * On success, returns :class:`OnlineInterviewWithHostLink` (the
 * one-shot post-mint response that includes ``start_url``). The
 * caller is responsible for surfacing the host link via clipboard
 * copy + toast, NOT for persisting it. Subsequent host-link
 * lookups go through :func:`useHostLink`, which the BE
 * audit-logs.
 */
export function useMintInterview(enquiryId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MintMeetingRequest) =>
      api.post<MintMeetingResponse>(
        `/admin/admissions/${encodeURIComponent(enquiryId ?? "")}/interviews:mint`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineInterviewKeys.all });
      // The stage-status read model rolls up interview completion
      // through ``interview_completed`` requirements; bust it so
      // the requirement chip refreshes immediately even though
      // the row is still SCHEDULED (counts as "in_progress").
      void qc.invalidateQueries({ queryKey: ["admissions", "stage-status", enquiryId] });
    },
  });
}

/**
 * Cancel a SCHEDULED interview. BE refuses if the row is already
 * STARTED / ENDED / CANCELLED / ERRORED (409). The local row is
 * always flipped to CANCELLED on success; the upstream Zoom
 * cancel is best-effort.
 */
export function useCancelInterview(interviewId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CancelInterviewRequest) =>
      api.post<CancelInterviewResponse>(
        `/admin/admissions/interviews/${encodeURIComponent(interviewId ?? "")}:cancel`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onlineInterviewKeys.all });
    },
  });
}

/**
 * Fetch the host ``start_url`` for one interview. Audit-logged
 * server-side: every call lands an
 * ``online_interview.host_link_accessed`` row. The IA UI exposes
 * this via an explicit "Copy host link" affordance — never an
 * automatic prefetch — so the audit trail accurately reflects
 * intentional access.
 */
export function useFetchHostLink() {
  return useMutation({
    mutationFn: async (interviewId: string) =>
      api.get<HostLinkResponse>(
        `/admin/admissions/interviews/${encodeURIComponent(interviewId)}/host-link`,
      ),
  });
}
